import { Check, ChevronsUpDown, GalleryVerticalEnd } from "lucide-react";
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { MyFirmListItem } from "@/lib/types";
import { selectActiveFirm } from "@/store/appSelectors";
import { useDispatch, useSelector } from "react-redux";
import { appActions } from "@/store/appSlice";
import { useMyFirmList } from "@/hooks/useMyFirmList";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export const FirmSwitcher = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { firmList, isLoading } = useMyFirmList();
  const { isMobile } = useSidebar();
  const activeFirm = useSelector(selectActiveFirm);
  const [open, setOpen] = useState(false);

  const onSelectFirm = (firm: MyFirmListItem) => {
    dispatch(appActions.setActiveFirm(firm));
    document.location.href = "/";
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <GalleryVerticalEnd className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {activeFirm?.firmName} {isLoading ? "..." : ""}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto opacity-50" />
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] min-w-56 p-0"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <Command>
              <CommandInput placeholder={t("common.search")} />
              <CommandList>
                <CommandEmpty>{t("dataTable.noResults")}</CommandEmpty>
                <CommandGroup heading={t("Firms")}>
                  {firmList.map((firm) => (
                    <CommandItem
                      key={firm.firmName}
                      value={firm.firmName}
                      onSelect={() => {
                        onSelectFirm(firm);
                        setOpen(false);
                      }}
                      className="gap-2 p-2"
                    >
                      <div className="flex size-6 items-center justify-center rounded-sm border">
                        <GalleryVerticalEnd className="size-4 shrink-0" />
                      </div>
                      {firm.firmName}
                      <Check
                        className={cn("ml-auto", activeFirm?.firmName === firm.firmName ? "opacity-100" : "opacity-0")}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
